import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import API from '@/lib/api';
import { useConfirm } from '@/components/ConfirmDialog';
import { cashError } from '@/lib/cashReceipts';
import { SCAN_STORAGE_KEY, cashLineToScan, cashScanLine, cashSaveReturn, readScanDraft, receiptEditMeta } from '@/lib/cashScan';
import { detecteerBtwRegel, klantStandaardBtw } from '@/utils/btwKlant';

export const useCashScanEditor = ({ id, access, items, setItems, klantId, setKlantId, customer }) => {
  const navigate = useNavigate(), location = useLocation(), confirm = useConfirm();
  const [meta, setMeta] = useState(null), [datum, setDatum] = useState(''), [pending, setPending] = useState(null);
  const [loading, setLoading] = useState(!!id), [saving, setSaving] = useState(false), [error, setError] = useState('');
  const [conflict, setConflict] = useState(false), [reload, setReload] = useState(0), [storageError, setStorageError] = useState('');
  const [preview, setPreview] = useState(null), [previewError, setPreviewError] = useState('');
  const [verified, setVerified] = useState(false);
  const lock = useRef(false), finished = useRef(false), fresh = useRef(false);
  const returnTo = useRef(location.state?.cashReturnTo);
  const canEdit = verified && access.allowed && access.writable && meta?.identity === access.identity;
  const override = detecteerBtwRegel(customer) != null ? 0 : klantStandaardBtw(customer);
  const body = useMemo(() => ({ datum, klant_id: klantId, lijnen: items.map(i => cashScanLine(i, override)) }), [datum, klantId, items, override]);
  const valid = /^\d{4}-\d{2}-\d{2}$/.test(datum) && items.length > 0 && body.lijnen.every(l => Number.isFinite(l.aantal) && l.aantal !== 0 && Number.isFinite(l.prijs) && l.prijs >= 0 && !!l.omschr?.trim());
  const dirty = !!meta && JSON.stringify(body) !== JSON.stringify(meta.original);
  const snapshot = request => ({ items, klantId, ts: Date.now(), cashEdit: { ...meta, datum, pending: request } });

  useEffect(() => {
    if (!id) return;
    let current = true;
    const controller = new AbortController();
    setVerified(false); setLoading(true); setError(''); setConflict(false);
    (async () => {
      try {
        if (!access.allowed || !access.writable) throw new Error('Geen toegang om dagontvangsten te bewerken.');
        const receipt = (await API.get(`/dagontvangsten/${encodeURIComponent(id)}`, { noCache: true, signal: controller.signal })).data;
        if (!current) return;
        const cached = readScanDraft();
        const same = cached?.cashEdit?.id === id && cached.cashEdit.identity === access.identity && Array.isArray(cached.items)
          && Array.isArray(cached.cashEdit.original?.lijnen) && Number.isInteger(cached.cashEdit.version);
        if (receipt.status !== 'opgeslagen' && !(same && cached.cashEdit.pending)) throw new Error('Deze ontvangst moet eerst worden afgerond via Dagontvangsten.');
        if (!same && (cached?.items?.length || cached?.klantId || cached?.cashEdit)) {
          const approved = await confirm({ title: 'Huidige scanlijst vervangen?', tone: 'warning',
            message: 'De huidige scanlijst wordt vervangen door deze dagontvangst. Niet-opgeslagen wijzigingen in die lijst gaan verloren.',
            confirmText: 'Scanlijst vervangen', cancelText: 'Annuleren' });
          if (!current) return;
          if (!approved) { finished.current = true; navigate(receiptEditMeta(receipt, access.identity, returnTo.current).returnTo, { replace: true }); return; }
          // Another tab may have changed the cart while confirmation was open.
          if (JSON.stringify(readScanDraft()) !== JSON.stringify(cached)) throw new Error('De scanlijst is intussen gewijzigd. Probeer opnieuw.');
        }
        const resume = same && !fresh.current;
        const nextMeta = resume ? cached.cashEdit : receiptEditMeta(receipt, access.identity, returnTo.current);
        const nextItems = resume ? cached.items : receipt.lijnen.map(cashLineToScan);
        const nextDate = resume ? cached.cashEdit.datum : receipt.datum;
        const nextCustomer = resume ? cached.klantId : receipt.klant_id || '';
        const nextPending = resume ? cached.cashEdit.pending || null : null;
        localStorage.setItem(SCAN_STORAGE_KEY, JSON.stringify({ items: nextItems, klantId: nextCustomer, ts: Date.now(), cashEdit: { ...nextMeta, datum: nextDate, pending: nextPending } }));
        if (!current) return;
        finished.current = false; fresh.current = false;
        setMeta(nextMeta); setItems(nextItems); setKlantId(nextCustomer); setDatum(nextDate); setPending(nextPending);
        setVerified(true);
        if (resume && receipt.version !== nextMeta.version && !nextPending) {
          setConflict(true); setError('Deze dagontvangst is elders gewijzigd. Je invoer blijft bewaard; laad de actuele ontvangst opnieuw voordat je opslaat.');
        }
      } catch (e) { if (current) setError(e.response ? cashError(e) : e.message || 'De ontvangst kon niet worden geladen.'); }
      finally { if (current) setLoading(false); }
    })();
    return () => { current = false; controller.abort(); };
  }, [id, access.identity, access.allowed, access.writable, reload, confirm, navigate, setItems, setKlantId]);

  useEffect(() => {
    if (!id || !meta || !canEdit || loading || finished.current) return;
    try { localStorage.setItem(SCAN_STORAGE_KEY, JSON.stringify(snapshot(pending))); setStorageError(''); }
    catch { setStorageError('Je wijzigingen konden niet op dit apparaat worden bewaard. Houd deze pagina open.'); }
  }, [id, meta, items, klantId, datum, pending, loading, canEdit]);

  useEffect(() => {
    setPreview(null); setPreviewError('');
    if (!id || !meta || !canEdit || loading || !valid || pending || conflict) return;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try { const r = await API.post('/dagontvangsten/preview', { ...body, request_id: crypto.randomUUID(), version: meta.version }, { params: { ontvangst_id: id }, signal: controller.signal }); if (!controller.signal.aborted) setPreview(r.data); }
      catch (e) { if (!controller.signal.aborted) setPreviewError(cashError(e)); }
    }, 250);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [id, meta, body, loading, valid, pending, conflict, canEdit]);

  const reloadReceipt = async () => {
    if (lock.current) return;
    if (meta && !await confirm({ title: 'Actuele dagontvangst laden?', message: 'De bewerkingen op deze pagina worden vervangen door de opgeslagen ontvangst.', confirmText: 'Opnieuw laden', cancelText: 'Blijven', tone: 'warning' })) return;
    fresh.current = true; setReload(v => v + 1);
  };
  const clearOwnDraft = request => {
    const cached = readScanDraft();
    if (cached?.cashEdit?.id === id && cached.cashEdit.identity === access.identity && (!request || cached.cashEdit.pending?.request_id === request)) localStorage.removeItem(SCAN_STORAGE_KEY);
  };
  const cancel = async () => {
    if (lock.current) return;
    if ((dirty || pending) && !await confirm({ title: 'Bewerken afsluiten?', message: pending ? 'Opslaan is nog niet bevestigd. Controleer de ontvangst in het overzicht voordat je verdergaat.' : 'De wijzigingen worden niet opgeslagen. De opgeslagen dagontvangst blijft behouden.', confirmText: 'Afsluiten', cancelText: 'Blijven', tone: 'warning' })) return;
    finished.current = true; clearOwnDraft(); navigate(meta?.returnTo || '/dagontvangsten', { replace: true });
  };
  const save = async () => {
    if (lock.current || !meta || !canEdit || conflict || loading || (!pending && !valid)) return;
    lock.current = true; setSaving(true); setError('');
    const request = pending || { request_id: crypto.randomUUID(), version: meta.version, ...body };
    try {
      // Persist the exact retry body before PUT. An ambiguous response never causes a second receipt.
      localStorage.setItem(SCAN_STORAGE_KEY, JSON.stringify(snapshot(request))); setPending(request);
      const receipt = (await API.put(`/dagontvangsten/${encodeURIComponent(id)}`, request, { timeout: 30000 })).data;
      finished.current = true; clearOwnDraft(request.request_id);
      toast.success(`Dagontvangst ${receipt.nummer} bijgewerkt.`);
      navigate(cashSaveReturn(meta, receipt.datum), { replace: true });
    } catch (e) {
      if (e.response && e.response.status < 500) { setPending(null); if (e.response.status === 409) setConflict(true); }
      setError(e.response ? cashError(e) : 'Geen bevestiging ontvangen. Probeer opnieuw met dezelfde gegevens; er wordt geen nieuwe bon gemaakt.');
    } finally { lock.current = false; setSaving(false); }
  };
  return { meta: canEdit ? meta : null, datum: canEdit ? datum : '', setDatum, loading, saving, pending, conflict, valid, dirty, save, cancel, reloadReceipt,
    error: error || storageError, preview: canEdit ? preview : null, previewError, ready: !!canEdit && !loading, locked: !canEdit || loading || saving || !!pending || conflict };
};